import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Button } from '@components';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

import {
    nhanVienGetAllAction,
    nhanVienLoadFormDataAction,
    nhanVienUpdateAction
} from '@app/store/reducers/nhanVien';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { VAI_TRO_OPTIONS, mapVaiTroLabel } from '@app/store/constants';
import { toast } from 'react-toastify';
import { messageToast } from '@app/utils/define';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const NhanVienEdit = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const query = useQuery();

    const idParam = query.get('id');
    const id = idParam ? parseInt(idParam, 10) : NaN;

    const { list, boPhans, teams } = useSelector((state) => state.nhanVien);
    const [current, setCurrent] = useState(null);

    const [boPhanId, setBoPhanId] = useState('');
    const [teamId, setTeamId] = useState('');
    const [vaiTro, setVaiTro] = useState(VAI_TRO_OPTIONS[0].value);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!list || list.length === 0) {
            dispatch(nhanVienGetAllAction());
        }
        dispatch(nhanVienLoadFormDataAction());
    }, [dispatch, list]);

    useEffect(() => {
        if (Number.isNaN(id) || !Array.isArray(list)) return;
        const found = list.find((x) => x.id === id);
        if (found) {
            setCurrent(found);
            setBoPhanId(
                typeof found.boPhanId === 'number'
                    ? String(found.boPhanId)
                    : ''
            );
            setTeamId(
                typeof found.teamId === 'number' ? String(found.teamId) : ''
            );

            const match = VAI_TRO_OPTIONS.find(
                (opt) =>
                    opt.value === found.vaiTro || opt.key === found.vaiTro
            );
            setVaiTro(match ? match.value : VAI_TRO_OPTIONS[0].value);
        }
    }, [list, id]);

    const filteredTeams = useMemo(() => {
        if (!boPhanId) return teams || [];
        return (teams || []).filter(
            (t) => String(t.boPhanId) === String(boPhanId)
        );
    }, [teams, boPhanId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (Number.isNaN(id)) {
            toast.error('Thiếu id nhân viên');
            return;
        }
        if (!boPhanId) {
            toast.error('Vui lòng chọn Bộ phận.');
            return;
        }

        const apiData = {
            boPhanId: Number(boPhanId),
            teamId: teamId ? Number(teamId) : null,
            vaiTro: Number(vaiTro)
        };

        const selectedBoPhan =
            Array.isArray(boPhans) &&
            boPhans.find((bp) => String(bp.id) === String(boPhanId));

        const selectedTeam =
            Array.isArray(teams) &&
            teams.find((t) => String(t.id) === String(teamId));

        // viewData chỉ dùng cho FE để cập nhật lại list, không ảnh hưởng API
        const viewData = {
            boPhanName: selectedBoPhan
                ? selectedBoPhan.tenBoPhan || selectedBoPhan.name
                : current?.boPhanName,
            teamName: selectedTeam
                ? selectedTeam.tenTeam || selectedTeam.name
                : current?.teamName
        };

        try {
            setSubmitting(true);

            await dispatch(
                nhanVienUpdateAction({ id, data: apiData, viewData })
            ).unwrap();

            toast.success(messageToast.messageToastUpdateSuccess);
            history.push(URI_INTERNAL.NHANVIEN_PATH);
        } catch (err) {
            console.error('Cập nhật nhân viên lỗi:', err);

            let msg =
                (err && err.message) ||
                (typeof err === 'string' ? err : '') ||
                (err && err.title) ||
                '';

            if (!msg && err && err.errors) {
                msg = Object.values(err.errors).flat().join('; ');
            }

            toast.error(
                msg
                    ? `${messageToast.messageToastUpdateError}: ${msg}`
                    : messageToast.messageToastUpdateError
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        history.push(URI_INTERNAL.NHANVIEN_PATH);
    };

    if (Number.isNaN(id)) {
        return (
            <Card>
                <Card.Header as="h5">Sửa nhân viên</Card.Header>
                <Card.Body>Thiếu id</Card.Body>
            </Card>
        );
    }

    if (!current) {
        return (
            <Card>
                <Card.Header as="h5">Sửa nhân viên</Card.Header>
                <Card.Body>
                    Đang tải thông tin nhân viên hoặc không tìm thấy nhân viên.
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Header as="h5">Sửa nhân viên</Card.Header>
            <Card.Body>
                <Form noValidate onSubmit={handleSubmit}>
                    {/* Thông tin người dùng (readonly) */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Họ tên
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                type="text"
                                value={current.hoTen || ''}
                                readOnly
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Email
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                type="text"
                                value={current.email || ''}
                                readOnly
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Số điện thoại
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                type="text"
                                value={current.sdt || ''}
                                readOnly
                            />
                        </Col>
                    </Form.Group>

                    {/* Bộ phận */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Bộ phận <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={boPhanId}
                                onChange={(e) => {
                                    setBoPhanId(e.target.value);
                                    setTeamId('');
                                }}
                            >
                                <option value="">
                                    -- Chọn bộ phận --
                                </option>
                                {(boPhans || []).map((bp) => (
                                    <option key={bp.id} value={bp.id}>
                                        {bp.tenBoPhan || bp.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Col>
                    </Form.Group>

                    {/* Team */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Tổ / Nhóm
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={teamId ?? ''}
                                onChange={(e) => setTeamId(e.target.value)}
                                disabled={!boPhanId}
                            >
                                <option value="">
                                    -- Không thuộc team --
                                </option>
                                {filteredTeams.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.tenTeam || t.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Col>
                    </Form.Group>

                    {/* Vai trò */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Vai trò <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={vaiTro}
                                onChange={(e) =>
                                    setVaiTro(Number(e.target.value))
                                }
                            >
                                {VAI_TRO_OPTIONS.map((opt) => (
                                    <option
                                        key={opt.value}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Control>
                            <small className="text-muted">
                                Vai trò hiện tại:{' '}
                                {mapVaiTroLabel(current.vaiTro)}
                            </small>
                        </Col>
                    </Form.Group>

                    {/* Buttons */}
                    <Form.Group as={Row} className="mb-3">
                        <Col md={{ span: 10, offset: 2 }}>
                            <ButtonGroup>
                                <Button
                                    type="submit"
                                    theme="primary"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Đang lưu...' : 'Lưu'}
                                </Button>
                                &nbsp;&nbsp;&nbsp;
                                <Button
                                    type="button"
                                    theme="secondary"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    Hủy
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Form.Group>
                </Form>

                <ReactSpinner loadings={submitting} />
            </Card.Body>
        </Card>
    );
};

export default NhanVienEdit;
